import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, Link } from "react-router-dom";
import { Bot, Send, X, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings, waUrl } from "@/context/SettingsContext";
import MicButton from "@/components/MicButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sessionId = () => {
  let id = sessionStorage.getItem("gal_chat_session");
  if (!id) { id = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`; sessionStorage.setItem("gal_chat_session", id); }
  return id;
};

const AiChatWidget = () => {
  const { lang, t } = useLanguage();
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const a = t.ai.chat;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, loading]);
  if (settings.assistant?.chat === false || pathname.startsWith("/admin")) return null;

  const welcome = settings.assistant?.welcome || a.welcome;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.slice(-8);
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai/chat`, { session_id: sessionId(), message: msg, history, lang, path: pathname });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (err) {
      const d = err.response?.data?.detail;
      setMessages((m) => [...m, { role: "assistant", text: typeof d === "string" ? d : a.error, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="ai-chat-toggle" aria-label={a.title} className="fixed bottom-6 right-24 z-50 flex h-14 items-center gap-2 rounded-full bg-forest px-5 text-white shadow-xl shadow-forest/30 transition-transform duration-300 hover:scale-105">
        {open ? <X className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
        <span className="hidden font-mono text-[11px] uppercase tracking-[0.15em] sm:inline">{a.button}</span>
      </button>

      {open && (
        <div data-testid="ai-chat-panel" className="fixed bottom-24 right-4 z-50 flex w-[min(92vw,380px)] flex-col overflow-hidden rounded-3xl border border-brand/15 bg-white shadow-2xl" style={{ height: "min(70vh, 560px)" }}>
          <div className="flex items-center gap-3 bg-forest px-5 py-4 text-white">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold"><Bot className="h-5 w-5" /></span>
            <div>
              <p className="font-display text-sm font-bold">{a.title}</p>
              <p className="text-[11px] text-white/70">{settings.company_name}</p>
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto bg-cream/60 px-4 py-4" data-testid="ai-chat-messages">
            <Bubble role="assistant" text={welcome} />
            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2" data-testid="ai-chat-suggestions">
                {a.suggestions.map((s) => (
                  <button type="button" key={s} onClick={() => send(s)} className="rounded-full border border-brand/30 bg-white px-3 py-1.5 text-xs text-forest transition-colors hover:border-gold hover:text-gold">{s}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.text} error={m.error} index={i} />)}
            {loading && <div className="flex items-center gap-2 text-xs text-brand" data-testid="ai-chat-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {a.typing}</div>}
            <div ref={endRef} />
          </div>

          <div className="border-t border-brand/10 bg-white px-3 py-3">
            <div className="flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder={a.placeholder} data-testid="ai-chat-input" className="w-full rounded-full border border-brand/20 px-4 py-2.5 text-sm text-forest outline-none focus:border-gold" />
              <MicButton compact testid="ai-chat-mic" onText={(text) => send(text)} />
              <button type="button" onClick={() => send()} disabled={loading || !input.trim()} data-testid="ai-chat-send" aria-label={a.send} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold text-white transition-colors hover:bg-gold-hover disabled:opacity-50"><Send className="h-4 w-4" /></button>
            </div>
            <p className="mt-2 text-center text-[10px] text-brand/70">
              {a.footer} <a href={waUrl(settings)} target="_blank" rel="noopener noreferrer" className="underline hover:text-gold">WhatsApp</a> · <Link to="/contact" onClick={() => setOpen(false)} className="underline hover:text-gold">{a.contact}</Link>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

const Bubble = ({ role, text, error, index }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`} data-testid={index !== undefined ? `ai-chat-msg-${index}` : "ai-chat-welcome"}>
    <p className={`max-w-[85%] whitespace-pre-line rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${role === "user" ? "rounded-br-md bg-gold text-white" : error ? "rounded-bl-md bg-red-50 text-red-700" : "rounded-bl-md bg-white text-forest shadow-sm"}`}>{text}</p>
  </div>
);

export default AiChatWidget;
